/**
 * @architecture-header 1
 * @layer frontend-package
 * @domain api-client
 * @responsibility Create an environment-neutral authenticated JSON transport for generated operations.
 * @state-owner request-local transport state
 * @allowed-imports same package error normalizer; platform fetch interface
 * @forbidden Vite env; Electron API; Supabase singleton; React; query cache
 * @public-api @vispo/api-client
 * @side-effects authenticated HTTP requests
 * @tests create-client.test.ts
 */

import { normalizeApiError } from './errors';

export interface ClientOptions {
  baseUrl: string;
  getAccessToken: () => Promise<string | null>;
  refreshAccessToken?: () => Promise<string | null>;
  fetch?: typeof globalThis.fetch;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: HeadersInit;
  signal?: AbortSignal;
  responseType?: 'json' | 'text';
}

export interface VispoApiClient {
  request<T>(path: string, options?: RequestOptions): Promise<T>;
}

export function createVispoApiClient(options: ClientOptions): VispoApiClient {
  const fetchImpl = options.fetch ?? globalThis.fetch;
  const baseUrl = options.baseUrl.replace(/\/+$/, '');

  return {
    async request<T>(path: string, requestOptions: RequestOptions = {}) {
      const isNativeBody =
        typeof requestOptions.body === 'string' ||
        requestOptions.body instanceof FormData ||
        requestOptions.body instanceof Blob ||
        requestOptions.body instanceof URLSearchParams ||
        requestOptions.body instanceof ArrayBuffer;
      const requestBody =
        requestOptions.body === undefined
          ? undefined
          : isNativeBody
            ? (requestOptions.body as BodyInit)
            : JSON.stringify(requestOptions.body);
      const url = `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
      const send = (token: string | null) =>
        fetchImpl(url, {
          method: requestOptions.method ?? 'GET',
          body: requestBody,
          signal: requestOptions.signal,
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            ...(requestOptions.body === undefined || isNativeBody
              ? {}
              : { 'Content-Type': 'application/json' }),
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...requestOptions.headers,
          },
        });

      let response = await send(await options.getAccessToken());
      if (response.status === 401 && options.refreshAccessToken) {
        const refreshedToken = await options.refreshAccessToken();
        if (refreshedToken) response = await send(refreshedToken);
      }

      const body =
        response.status === 204
          ? undefined
          : requestOptions.responseType === 'text'
            ? await response.text()
            : await response.json().catch(() => undefined);
      if (!response.ok) throw normalizeApiError(body, response.status);
      return body as T;
    },
  };
}
